import React, { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { useBrand, BrandLogo } from "@/lib/brand";
import { Loader2, Lock, ExternalLink } from "lucide-react";

/**
 * Walkthrough embed view — `/embed/walkthrough/:id?k=<token>`.
 * Designed to be loaded inside an `<iframe>` on third-party landing pages
 * or ABM emails. Minimalist chrome: just the video + recorder name + a
 * small "Book a working session" CTA. Reuses the same heartbeat telemetry
 * as the public share page so analytics still flow.
 */
export default function WalkthroughEmbed() {
  const { id } = useParams();
  const [sp] = useSearchParams();
  const { resolveBy } = useBrand();
  const k = sp.get("k") || "";
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState("");
  const sessionIdRef = useRef("");
  const lastBeatRef = useRef(0);
  const playedRef = useRef(false);

  if (!sessionIdRef.current) {
    sessionIdRef.current = (typeof crypto !== "undefined" && crypto.randomUUID)
      ? crypto.randomUUID()
      : `embed-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }

  useEffect(() => {
    if (!id || !k) { setError("Missing embed token."); return; }
    let alive = true;
    resolveBy("walkthrough", id);
    (async () => {
      try {
        const r = await axios.get(`${API}/walkthroughs/${id}`, { params: { k } });
        if (alive) setMeta(r.data);
      } catch (e) {
        if (alive) setError(e?.response?.data?.detail || "Couldn't load walkthrough.");
      }
    })();
    return () => { alive = false; };
  }, [id, k]);

  const beat = async (t, completed = false) => {
    try {
      await axios.post(
        `${API}/walkthroughs/${id}/heartbeat`,
        { session_id: sessionIdRef.current, progress_s: t || 0, completed },
        { params: { k } },
      );
    } catch { /* swallow */ }
  };

  const onPlay = (e) => {
    if (playedRef.current) return;
    playedRef.current = true;
    beat(e.target.currentTime, false);
    // Notify host page via postMessage (parent frame can listen)
    try { window.parent?.postMessage({ source: "dara-walkthrough", event: "play", id }, "*"); } catch { /* noop */ }
  };
  const onTimeUpdate = (e) => {
    const t = e.target.currentTime;
    if (t - lastBeatRef.current > 5 || t < lastBeatRef.current) {
      lastBeatRef.current = t;
      beat(t, false);
    }
  };
  const onEnded = (e) => {
    beat(e.target.currentTime, true);
    try { window.parent?.postMessage({ source: "dara-walkthrough", event: "ended", id }, "*"); } catch { /* noop */ }
  };

  const videoSrc = id && k ? `${API}/walkthroughs/${id}/video?k=${encodeURIComponent(k)}` : "";

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black text-zinc-400 p-6 text-center">
        <div className="border border-[#FF3B30]/40 bg-[#FF3B30]/5 p-6 max-w-md">
          <Lock className="w-6 h-6 text-[#FF3B30] mx-auto mb-3" />
          <div className="font-mono text-[11px] uppercase tracking-widest text-[#FF3B30] mb-2">EMBED ACCESS DENIED</div>
          <div className="font-mono text-[11px]">{error}</div>
        </div>
      </div>
    );
  }
  if (!meta) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black text-zinc-500 font-mono text-[11px] gap-2">
        <Loader2 className="w-4 h-4 animate-spin text-[#FFB000]" /> LOADING…
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white" data-testid="walkthrough-embed-page">
      <div className="flex flex-col h-screen">
        <video
          key={videoSrc}
          controls
          autoPlay
          onPlay={onPlay}
          onTimeUpdate={onTimeUpdate}
          onEnded={onEnded}
          src={videoSrc}
          className="flex-1 w-full bg-black"
          data-testid="walkthrough-embed-video"
        />
        <div className="border-t border-zinc-900 px-4 py-2.5 flex items-center justify-between gap-3">
          <div className="min-w-0 flex-1 flex items-center gap-3">
            <BrandLogo size="sm" />
            <div className="min-w-0">
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#FFB000]">// WALKTHROUGH</div>
              <div className="font-heading font-bold text-sm truncate text-white">{meta.title}</div>
              <div className="font-mono text-[10px] text-zinc-500 truncate">
                by {meta.user_name}{meta.company ? ` · ${meta.company}` : ""}
              </div>
            </div>
          </div>
          <a
            href={`/?intent=enterprise#contact`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="walkthrough-embed-cta"
            style={{ backgroundColor: "var(--brand-accent, #FFB000)", color: "var(--brand-accent-text, #000)" }}
            className="font-bold font-mono text-[10px] uppercase tracking-widest px-3 py-2 hover:opacity-90 flex items-center gap-1.5 flex-shrink-0 transition-opacity"
          >
            <ExternalLink className="w-3 h-3" /> BOOK A SESSION
          </a>
        </div>
      </div>
    </div>
  );
}
